// A minimal disclosure popover: a trigger button that toggles a floating
// panel, closed by an outside click or Escape. Used for the header settings
// and the chart Export menu — action menus and switch clusters, not modals.

import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/**
 * The lowest point a panel may reach, in viewport coordinates: the bottom of
 * what is actually visible (the visual viewport, which an on-screen keyboard
 * shortens), and — unless the panel draws over it — the top of the fixed
 * transport bar.
 */
/** The least room a panel keeps from either side of the screen. */
const EDGE = 8;

function panelFloor(overTransport: boolean): number {
  const viewport = window.visualViewport;
  const visible = viewport
    ? Math.min(window.innerHeight, viewport.offsetTop + viewport.height)
    : window.innerHeight;
  if (overTransport) return visible;
  const bar = document.querySelector('.transport-bar');
  return bar ? Math.min(visible, bar.getBoundingClientRect().top) : visible;
}

export function Popover({
  label,
  title,
  align = 'left',
  testid,
  inlineOnMobile = false,
  overTransport = false,
  children,
}: {
  /** Trigger button content. */
  label: ReactNode;
  /** Tooltip / accessible label for the trigger. */
  title?: string;
  align?: 'left' | 'right';
  testid?: string;
  /** Keep the panel mounted while closed so responsive CSS can present its
   * contents inline instead of behind the disclosure on small screens. */
  inlineOnMobile?: boolean;
  /** The panel paints ABOVE the transport bar (its anchor sits in a higher
   * stacking layer — the workspace header), so it may use the screen down to
   * its foot rather than stopping at the bar. */
  overTransport?: boolean;
  /** Panel content; a function form receives a `close` callback (e.g. for a
   * form's submit button to dismiss the popover after acting). */
  children: ReactNode | ((close: () => void) => ReactNode);
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // A panel never runs off the screen or under the fixed transport bar. Its
  // anchors are mostly PINNED now (the workspace header, the view strip, the
  // studio's toolbar dock are sticky), so a tall menu opened from one cannot
  // be scrolled clear of the bar the way an in-flow menu could — the button at
  // its foot sat under the transport with no way to reach it (found by the
  // e2e: Tune details' Done under an open Repeats panel, the file menu's Clear
  // under the chip strip). Measured, not styled: the room is the distance from
  // the panel's top to the floor (panelFloor), re-read when the panel, the bar
  // or the visible viewport changes — the last is the phone's keyboard, which
  // shortens what is visible without resizing the window; a panel that fits
  // is left exactly as its CSS drew it.
  useLayoutEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel || typeof window === 'undefined') return;
    const fit = () => {
      // Sideways first: a panel anchored to a trigger near one edge of a
      // phone ran off the other (the Chords menu, right-anchored to a button
      // left of centre, opened 35px off the left of a 390px screen). Measure
      // where CSS put it and slide it back inside an 8px margin; a panel that
      // fits keeps its CSS position.
      panel.style.translate = '';
      const box = panel.getBoundingClientRect();
      const width = document.documentElement.clientWidth || window.innerWidth;
      if (box.width > 0) {
        let shift = 0;
        if (box.left < EDGE) shift = EDGE - box.left;
        else if (box.right > width - EDGE)
          shift = Math.max(EDGE - box.left, width - EDGE - box.right);
        if (shift) panel.style.translate = `${Math.round(shift)}px 0`;
      }
      const top = panel.getBoundingClientRect().top;
      const room = Math.floor(panelFloor(overTransport) - top - 12);
      if (room >= 120 && panel.scrollHeight > room) {
        panel.style.maxHeight = `${room}px`;
        panel.style.overflowY = 'auto';
        // Scrolling to the panel's end must not carry on into the page.
        panel.style.overscrollBehavior = 'contain';
      } else {
        panel.style.maxHeight = '';
        panel.style.overflowY = '';
        panel.style.overscrollBehavior = '';
      }
    };
    fit();
    window.addEventListener('resize', fit);
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', fit);
    viewport?.addEventListener('scroll', fit);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    observer?.observe(panel);
    const bar = document.querySelector('.transport-bar');
    if (bar && !overTransport) observer?.observe(bar);
    return () => {
      window.removeEventListener('resize', fit);
      viewport?.removeEventListener('resize', fit);
      viewport?.removeEventListener('scroll', fit);
      observer?.disconnect();
      panel.style.translate = '';
      panel.style.maxHeight = '';
      panel.style.overflowY = '';
      panel.style.overscrollBehavior = '';
    };
  }, [open, overTransport]);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div
      className={`popover-root${inlineOnMobile ? ' popover-inline-on-mobile' : ''}`}
      ref={rootRef}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`btn btn-sm popover-trigger${open ? ' active' : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={panelId}
        title={title}
        data-testid={testid}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
      </button>
      {(open || inlineOnMobile) && (
        <div
          id={panelId}
          ref={panelRef}
          className={`popover-panel popover-${align}${open ? '' : ' popover-panel-closed'}`}
          role="dialog"
          aria-label={title}
        >
          {typeof children === 'function' ? children(() => setOpen(false)) : children}
        </div>
      )}
    </div>
  );
}
